import { Typography, Box } from '@mui/material'
import { useState, useEffect } from 'react'
import { xrayTypes } from 'pages/Diagnostics/constants/diagnostic_types'
import { IExamResponse } from 'types/exam/types'
import { compressImageToObjectUrl, getProxiedImageUrl } from 'utils/image'

interface PrintXRayExamProps {
  examData: IExamResponse
  xrayAnnotations?: Record<string, string>
  /**
   * Disparado quando a imagem original terminou de carregar (com sucesso ou
   * não). A geração do PDF depende disso: sem o sinal, a captura acontece com
   * o bloco da imagem ainda vazio e o relatório sai com "Carregando..." no
   * lugar do raio-X.
   */
  onReady?: () => void
}

export const PrintXRayExam = ({
  examData,
  xrayAnnotations = {},
  onReady
}: PrintXRayExamProps) => {
  const [originalImage, setOriginalImage] = useState<string | null>(null)
  const [isReady, setIsReady] = useState(false)

  const activeAnnotations = Object.entries(xrayAnnotations).filter(
    ([_, annotation]) => annotation.trim() !== ''
  )

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null

    const loadOriginalImage = async () => {
      setIsReady(false)
      setOriginalImage(null)

      const originalUrl = getProxiedImageUrl(examData.link_original_data)

      if (!originalUrl) {
        setIsReady(true)
        return
      }

      try {
        objectUrl = await compressImageToObjectUrl(originalUrl, 600, 0.7)

        if (cancelled) {
          URL.revokeObjectURL(objectUrl)
          return
        }

        setOriginalImage(objectUrl)
      } catch (error) {
        console.error('Erro ao comprimir imagem original:', error)
        // Degrada para a URL direta: a imagem sai em resolução original, mas
        // sai. O `isReady` fica por conta do onLoad/onError do `<img>`.
        if (!cancelled) setOriginalImage(originalUrl)
      }
    }

    loadOriginalImage()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [examData])

  useEffect(() => {
    if (isReady) onReady?.()
  }, [isReady, onReady])

  return (
    <Box>
      {!isReady && (
        <Box sx={{ p: 2, textAlign: 'center', color: '#666' }}>
          <Typography variant='caption'>
            Preparando imagens para impressão...
          </Typography>
        </Box>
      )}

      <Box
        data-pdf-section
        sx={{
          mb: 2,
          pageBreakInside: 'avoid',
          pageBreakAfter: 'always'
        }}
      >
        <Typography
          variant='subtitle1'
          sx={{
            mb: 1.5,
            fontWeight: 'bold',
            fontSize: '1rem',
            textAlign: 'center'
          }}
        >
          Imagem original
        </Typography>
        <Box
          sx={{
            width: '100%',
            maxWidth: '400px',
            margin: '0 auto',
            border: '2px solid #ddd',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#000',
            minHeight: '500px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          {originalImage ? (
            <img
              src={originalImage}
              alt='Imagem Original do Raio-X'
              onLoad={() => setIsReady(true)}
              onError={() => setIsReady(true)}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
          ) : (
            <Typography sx={{ color: '#fff' }}>Carregando...</Typography>
          )}
        </Box>
      </Box>

      {/* Documentation: hidden while we are waiting for feebacks from the medical team.
          Ao reativar este bloco é preciso voltar a popular um mapa de imagens por
          `XRayDiagnosticCodesKeys` no useEffect acima (removido junto com o bloco por
          ter virado código morto) e reimportar `Grid2`, `React`,
          `getResultConfigByLabel`, `CXRAY_DECISION_THRESHOLD` e
          `XRayDiagnosticCodesKeys`.

      <Box
        data-pdf-section
        sx={{
          mb: 2,
          pageBreakInside: 'avoid',
          pageBreakAfter: 'always'
        }}
      >
        <Typography
          variant='subtitle1'
          sx={{ mb: 1.5, fontWeight: 'bold', fontSize: '1rem' }}
        >
          2. Análises
        </Typography>
        {(() => {
          const validDiagnostics = XRayDiagnosticCodesKeys.filter(key => {
            const examResult = examData.exam_results.find(
              r => r.result_type === key
            )
            return (examResult?.result_value ?? 0) >= CXRAY_DECISION_THRESHOLD
          })

          if (validDiagnostics.length === 0) {
            return (
              <Box
                sx={{
                  p: 2,
                  textAlign: 'center',
                  color: '#666',
                  backgroundColor: '#fafafa',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
              >
                <Typography variant='body2'>
                  Nenhuma análise encontrada
                </Typography>
              </Box>
            )
          }

          return (
            <Grid container spacing={1.5}>
              {XRayDiagnosticCodesKeys.map((key, i) => {
                const examResult = examData.exam_results.find(
                  r => r.result_type === key
                )

                if ((examResult?.result_value ?? 0) < CXRAY_DECISION_THRESHOLD) {
                  return <React.Fragment key={'empty-' + i}></React.Fragment>
                }

                const result = getResultConfigByLabel(
                  examResult?.label_result_value
                )
                const imageUrl = compressedImages[key]
                const typeData = xrayTypes[key]

                return (
                  <Grid key={'print-xray-' + i} size={{ xs: 12, sm: 3 }}>
                    <Box
                      sx={{
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        p: 0.8,
                        backgroundColor: '#fafafa',
                        pageBreakInside: 'avoid',
                        height: '210px'
                      }}
                    >
                      <Typography
                        variant='caption'
                        sx={{
                          fontWeight: 'bold',
                          fontSize: '0.65rem',
                          display: 'block',
                          mb: 0.3
                        }}
                      >
                        {typeData.name}
                      </Typography>
                      {imageUrl && (
                        <Box
                          sx={{
                            width: '100%',
                            height: '155px',
                            overflow: 'hidden',
                            borderRadius: '4px',
                            mb: 0.3,
                            backgroundColor: '#000'
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={typeData.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                      )}
                      <Typography
                        variant='caption'
                        sx={{
                          fontSize: '0.6rem',
                          color: result.color,
                          fontWeight: 'bold'
                        }}
                      >
                        {result.label}
                      </Typography>
                    </Box>
                  </Grid>
                )
              })}
            </Grid>
          )
        })()}
      </Box> */}

      {/* Cada anotação é uma seção de PDF própria, em vez de um bloco único com
          todas dentro: o gerador não fatia uma seção maior que a página, então
          um bloco único cresceria com o número de comentários até estourar a
          página e ter o excesso cortado. */}
      {activeAnnotations.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box
            data-pdf-section
            sx={{
              mb: 1.5,
              pageBreakInside: 'avoid',
              pageBreakAfter: 'avoid',
              pageBreakBefore: 'always'
            }}
          >
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 'bold', fontSize: '1rem' }}
            >
              Anotações Médicas
            </Typography>
          </Box>

          {activeAnnotations.map(([imageId, annotation]) => {
            const imageName = xrayTypes[imageId]?.name || imageId

            return (
              <Box
                key={imageId}
                data-pdf-section
                sx={{
                  mb: 2,
                  p: 1.5,
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa',
                  pageBreakInside: 'avoid'
                }}
              >
                <Typography
                  variant='subtitle2'
                  sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}
                >
                  {imageName}:
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                    color: 'rgba(0, 0, 0, 0.87)'
                  }}
                >
                  {annotation}
                </Typography>
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

export default PrintXRayExam
